import { useId } from 'react';
import { type UserRegion } from '@pomoshnik/shared';

/**
 * Phone input с региональной маской. Block 0 Phase 2 / Step 3 (Q2 ✅).
 *
 * Маска по `region`:
 *   - KZ/RU: `+7 (___) ___-__-__` — 10 национальных цифр после `+7`.
 *   - UZ:    `+998 (__) ___-__-__` — 9 национальных цифр после `+998`.
 *
 * Контракт props:
 *   - `value` — E.164 строка («+71234567890» / «+998901234567») или `''`.
 *   - `onChange(e164)` — отдаёт E.164: `prefix + national` для любого ввода
 *     (даже частичного). Завершённость валидирует Zod в Container.
 *
 * Распарсивание:
 *   - Снимает все нецифры, опционально страйпит ведущий country code, обрезает
 *     до `nationalLen`. Country code снимается только если `length > nationalLen`,
 *     иначе номера, чьи национальные цифры начинаются с `7` (KZ-мобильные),
 *     теряли бы первую цифру.
 *
 * P-022 a11y baseline — как у TextInput (label htmlFor / aria-invalid /
 * aria-describedby / focus-visible / min-h-tap).
 *
 * Замечание: при смене `region` на лету (например, пользователь меняет город
 * Москва → Ташкент в Stage 2) `value` может стать невалидным под новый
 * формат. Container обязан явно сбрасывать `value=""` при смене region;
 * примитив сам этого не делает (controlled).
 */
export interface PhoneInputProps {
  name: string;
  label: string;
  region: UserRegion;
  /** E.164 формат: «+7XXXXXXXXXX» / «+998XXXXXXXXX» или ''. */
  value: string;
  /** Колбэк отдаёт E.164 (даже неполный). */
  onChange: (e164: string) => void;
  error?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
  autoComplete?: string;
}

type RegionConfig = { prefix: string; cc: string; nationalLen: number; mask: string };

// `cc` — country code без `+`. `mask` — placeholder-string с `D` под цифру.
const REGION_CONFIG: Record<UserRegion, RegionConfig> = {
  KZ: { prefix: '+7', cc: '7', nationalLen: 10, mask: '+7 (DDD) DDD-DD-DD' },
  RU: { prefix: '+7', cc: '7', nationalLen: 10, mask: '+7 (DDD) DDD-DD-DD' },
  UZ: { prefix: '+998', cc: '998', nationalLen: 9, mask: '+998 (DD) DDD-DD-DD' },
};

/**
 * Извлекает национальные цифры из произвольного ввода или E.164 value.
 *
 * Алгоритм:
 *   1. Если строка буквально начинается с региональным `prefix` (`+7` / `+998`),
 *      срезаем префикс и берём цифры из остатка. Это покрывает все нормальные
 *      случаи — наша маска всегда рендерит префикс, и controlled value тоже
 *      приходит как E.164 (тоже с префиксом).
 *   2. Fallback (paste без `+` или с пробельным префиксом «8 ...»): если общий
 *      digit-stream начинается с `cc` И длиннее `nationalLen`, страйпим — иначе
 *      KZ-номера 700/701/707/747/771/776/777/778 потеряли бы первую `7`.
 */
function extractNational(
  rawInput: string,
  prefix: string,
  cc: string,
  nationalLen: number,
): string {
  if (rawInput.startsWith(prefix)) {
    return rawInput.slice(prefix.length).replace(/\D/g, '').slice(0, nationalLen);
  }
  const allDigits = rawInput.replace(/\D/g, '');
  if (allDigits.startsWith(cc) && allDigits.length > nationalLen) {
    return allDigits.slice(cc.length).slice(0, nationalLen);
  }
  return allDigits.slice(0, nationalLen);
}

/** Подставляет цифры в маску; недостающие позиции — `_`. */
function applyMask(mask: string, digits: string): string {
  let i = 0;
  return mask.replace(/D/g, () => digits[i++] ?? '_');
}

export function PhoneInput({
  name,
  label,
  region,
  value,
  onChange,
  error,
  hint,
  disabled,
  required,
  autoComplete = 'tel',
}: PhoneInputProps) {
  const reactId = useId();
  const inputId = `${name}-${reactId}`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  const { prefix, cc, nationalLen, mask } = REGION_CONFIG[region];

  const nationalDigits = extractNational(value, prefix, cc, nationalLen);
  const displayValue = applyMask(mask, nationalDigits);

  const describedBy = error ? errorId : hint ? hintId : undefined;
  const hasError = Boolean(error);

  const handleChange = (raw: string) => {
    const next = extractNational(raw, prefix, cc, nationalLen);
    // Пустые цифры → отдаём ''; иначе E.164 с префиксом.
    onChange(next.length === 0 ? '' : `${prefix}${next}`);
  };

  return (
    <div className="flex flex-col">
      <label htmlFor={inputId} className="text-ink font-semibold text-base mb-2">
        {label}
      </label>
      <input
        id={inputId}
        name={name}
        type="tel"
        value={displayValue}
        onChange={(e) => handleChange(e.target.value)}
        autoComplete={autoComplete}
        inputMode="tel"
        disabled={disabled}
        required={required}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy}
        className={
          'min-h-tap rounded-xl bg-card text-ink text-base px-3 py-3 ' +
          'border-[1.5px] ' +
          (hasError ? 'border-error ' : 'border-accent ') +
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
          'disabled:bg-icon-bg disabled:text-muted disabled:cursor-not-allowed'
        }
      />
      {hasError ? (
        <p id={errorId} role="alert" className="text-error text-sm mt-1">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-muted text-sm mt-1">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
